import { createBrowserRouter, Navigate } from 'react-router-dom';
import App from './App';
import { DocumentsPage } from './pages/extraction/DocumentsPage';
import { DocumentDetailPage } from './pages/DocumentDetailPage';
import LoginPage from './pages/LoginPage';
import SelectContextPage from './pages/SelectContextPage';
import ProtectedRoute from './components/ProtectedRoute';
import AnalyticsPage from './pages/extraction/AnalyticsPage';
import { AnalysePage } from './pages/benchmark/AnalysePage';
import { ComparaisonPage } from './pages/benchmark/ComparaisonPage';
import { GroundTruthPage } from './pages/benchmark/GroundTruthPage';
import { GroundTruthEditPage } from './pages/benchmark/GroundTruthEditPage';
import { OptimisationPromptsPage } from './pages/benchmark/OptimisationPromptsPage';
import { OptimisationSessionPage } from './pages/benchmark/OptimisationSessionPage';
import { DomainsPage } from './pages/parametres/DomainsPage';
import { PromptsPage } from './pages/parametres/PromptsPage';
import { SchemasPage } from './pages/parametres/SchemasPage';
import { OcrConfigsPage } from './pages/parametres/OcrConfigsPage';
import { LlmConfigsPage } from './pages/parametres/LlmConfigsPage';
import { LlmModelsPage } from './pages/admin/LlmModelsPage';
import SchemaEditorPage from './pages/admin/SchemaEditorPage';
import ComptePage from './pages/ComptePage';
import { UtilisateursPage } from './pages/parametres/UtilisateursPage';
import { ClientsListPage } from './pages/parametres/ClientsListPage';
import { JobsPage } from './pages/parametres/JobsPage';
import { StatistiquesPage } from './pages/parametres/StatistiquesPage';
import { ClientEditPage } from './pages/parametres/ClientEditPage';
import MiradorBridgePage from './pages/integrations/MiradorBridgePage';
import { MonTenantPage } from './pages/MonTenantPage';
import { PollingProvider } from './contexts/PollingContext';

export const router = createBrowserRouter(
  [
    {
      path: '/login',
      element: <LoginPage />,
    },
    {
      path: '/select-context',
      element: <SelectContextPage />,
    },
    {
      path: '/',
      element: <App />,
      children: [
        {
          index: true,
          element: <Navigate to="/extraction/documents" replace />,
        },
        // Redirections des anciennes URLs
        {
          path: 'documents',
          element: <Navigate to="/extraction/documents" replace />,
        },
        {
          path: 'analytics',
          element: <Navigate to="/extraction/analytics" replace />,
        },
        {
          path: 'benchmark',
          element: <Navigate to="/benchmark/analyse" replace />,
        },
        {
          path: 'admin/prompts',
          element: <Navigate to="/parametres/prompts" replace />,
        },
        {
          path: 'admin/schemas',
          element: <Navigate to="/parametres/schemas" replace />,
        },
        {
          path: 'admin/ground-truth',
          element: <Navigate to="/benchmark/ground-truth" replace />,
        },
        // Mon tenant — admin_client ou superadmin
        {
          path: 'mon-tenant',
          element: (
            <ProtectedRoute requireAdminClient>
              <MonTenantPage />
            </ProtectedRoute>
          ),
        },
        // Compte (accessible à tous les rôles authentifiés)
        {
          path: 'compte',
          element: (
            <ProtectedRoute>
              <ComptePage />
            </ProtectedRoute>
          ),
        },
        // Routes inbox/documents — authentifié seulement
        {
          path: 'extraction/documents',
          element: (
            <ProtectedRoute>
              <DocumentsPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'extraction/analytics',
          element: (
            <ProtectedRoute requireAdminClient>
              <AnalyticsPage />
            </ProtectedRoute>
          ),
        },
        // Routes benchmark — superadmin
        {
          path: 'benchmark/analyse',
          element: (
            <ProtectedRoute requireSuperadmin>
              <AnalysePage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'benchmark/comparaison',
          element: (
            <ProtectedRoute requireSuperadmin>
              <ComparaisonPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'benchmark/ground-truth',
          element: (
            <ProtectedRoute requireSuperadmin>
              <GroundTruthPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'benchmark/optimisation',
          element: (
            <ProtectedRoute requireSuperadmin>
              <OptimisationPromptsPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'benchmark/optimisation/:sessionId',
          element: (
            <ProtectedRoute requireSuperadmin>
              <OptimisationSessionPage />
            </ProtectedRoute>
          ),
        },
        // Routes paramètres catalogue — superadmin
        {
          path: 'parametres/domaines',
          element: (
            <ProtectedRoute requireSuperadmin>
              <DomainsPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'parametres/prompts',
          element: (
            <ProtectedRoute requireSuperadmin>
              <PromptsPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'parametres/schemas',
          element: (
            <ProtectedRoute requireSuperadmin>
              <SchemasPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'parametres/ocr',
          element: (
            <ProtectedRoute requireSuperadmin>
              <OcrConfigsPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'parametres/llm',
          element: (
            <ProtectedRoute requireSuperadmin>
              <LlmConfigsPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'parametres/llm-models',
          element: (
            <ProtectedRoute requireSuperadmin>
              <LlmModelsPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'parametres/clients',
          element: (
            <ProtectedRoute requireSuperadmin>
              <ClientsListPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'parametres/clients/:id',
          element: (
            <ProtectedRoute requireSuperadmin>
              <ClientEditPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'parametres/jobs',
          element: (
            <ProtectedRoute requireSuperadmin>
              <JobsPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'parametres/statistiques',
          element: (
            <ProtectedRoute requireSuperadmin>
              <StatistiquesPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'parametres/utilisateurs',
          element: (
            <ProtectedRoute requireSuperadmin>
              <UtilisateursPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'parametres/integrations/mirador',
          element: (
            <ProtectedRoute requireSuperadmin>
              <MiradorBridgePage />
            </ProtectedRoute>
          ),
        },
        // Pages détail
        {
          path: 'benchmark/ground-truth/:id',
          element: (
            <ProtectedRoute requireSuperadmin>
              <GroundTruthEditPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'extraction/documents/:id',
          element: (
            <PollingProvider>
              <ProtectedRoute>
                <DocumentDetailPage />
              </ProtectedRoute>
            </PollingProvider>
          ),
        },
        {
          path: 'parametres/schemas/:id/edit',
          element: (
            <ProtectedRoute requireSuperadmin>
              <SchemaEditorPage />
            </ProtectedRoute>
          ),
        },
      ],
    },
  ],
  {
    // Préfixe de déploiement sous-chemin. Dev → '' (racine). Prod sous-chemin
    // (VITE_BASE=/dokimmo/) → '/dokimmo'. React Router strippe ce basename de
    // useLocation().pathname → les comparaisons existantes restent valides.
    basename: import.meta.env.BASE_URL.replace(/\/$/, ''),
    future: {
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  }
);
